import { useCallback } from 'react';
import SparkMD5 from 'spark-md5';

import {
  useEnterpriseAssetLibraryRequestUploadMutation,
  useEnterpriseAssetLibraryFinalizeAssetMutation,
  LibraryAssetType as GqlLibraryAssetType,
} from 'types/graphql';
import { useStores } from 'src/hooks/use-stores';

import type { LibraryAsset, RawLibraryAsset } from '../types';

const HASH_CHUNK_SIZE = 2 * 1024 * 1024;
// Abort an S3 PUT that makes no upload progress for this long (catches silent
// network drops without falsely aborting large-but-progressing video uploads).
const UPLOAD_STALL_TIMEOUT_MS = 60_000;

export type UploadProgress = {
  error?: string;
  percent: number;
  stage: 'done' | 'error' | 'finalizing' | 'hashing' | 'requesting' | 'uploading';
};

export type AssetDimensions = { width: number | null; height: number | null };

export type UploadResult = {
  asset: LibraryAsset;
  alreadyInLibrary: boolean;
};

const hexToBase64 = (hex: string): string => {
  const byteCount = hex.length / 2;
  const bytes = new Uint8Array(byteCount);
  for (let i = 0; i < byteCount; i += 1) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

export const hashFile = (file: File, onProgress?: (pct: number) => void): Promise<string> =>
  new Promise((resolve, reject) => {
    const spark = new SparkMD5.ArrayBuffer();
    const reader = new FileReader();
    let cursor = 0;

    const loadNext = () => {
      const slice = file.slice(cursor, cursor + HASH_CHUNK_SIZE);
      reader.readAsArrayBuffer(slice);
    };

    reader.onload = (e) => {
      const buffer = e.target?.result;
      if (!(buffer instanceof ArrayBuffer)) {
        reject(new Error('Failed to read file chunk'));
        return;
      }
      spark.append(buffer);
      cursor += buffer.byteLength;
      if (file.size > 0) onProgress?.(Math.min(100, Math.round((cursor / file.size) * 100)));
      if (cursor < file.size) {
        loadNext();
      } else {
        resolve(spark.end());
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Hash read failed'));

    loadNext();
  });

export const readDimensions = (file: File): Promise<AssetDimensions> => {
  if (file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
        URL.revokeObjectURL(url);
      };
      img.onerror = () => {
        resolve({ width: null, height: null });
        URL.revokeObjectURL(url);
      };
      img.src = url;
    });
  }
  if (file.type.startsWith('video/')) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve({ width: video.videoWidth || null, height: video.videoHeight || null });
        URL.revokeObjectURL(url);
      };
      video.onerror = () => {
        resolve({ width: null, height: null });
        URL.revokeObjectURL(url);
      };
      video.src = url;
    });
  }
  return Promise.resolve({ width: null, height: null });
};

const inferAssetType = (mimeType: string): GqlLibraryAssetType => {
  if (mimeType === 'image/svg+xml') return GqlLibraryAssetType.svg;
  if (mimeType.startsWith('video/')) return GqlLibraryAssetType.video;
  return GqlLibraryAssetType.image;
};

const buildAsset = (raw: RawLibraryAsset): LibraryAsset => ({
  id: raw.id,
  name: raw.name,
  fileName: raw.fileName,
  url: raw.url,
  contentHash: raw.contentHash,
  type: raw.type as LibraryAsset['type'],
  mimeType: raw.mimeType,
  width: raw.width ?? null,
  height: raw.height ?? null,
  fileSize: raw.fileSize,
  folderId: raw.folderId ?? null,
  tags: raw.tags ?? [],
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
  createdBy: raw.createdBy ?? null,
  aiGenerated: Boolean(raw.aiGenerated),
});

// Exported for unit testing the stall-timeout behavior in isolation.
export const putToS3 = (
  uploadUrl: string,
  file: File,
  contentHash: string,
  onPct: (pct: number) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer: ReturnType<typeof setTimeout>;
    const clearStall = (): void => clearTimeout(stallTimer);
    const armStall = (): void => {
      clearStall();
      stallTimer = setTimeout(() => {
        xhr.abort();
        reject(new Error('S3 upload stalled'));
      }, UPLOAD_STALL_TIMEOUT_MS);
    };

    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.setRequestHeader('Content-MD5', hexToBase64(contentHash));
    xhr.upload.onprogress = (e) => {
      armStall();
      if (e.lengthComputable) onPct(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      clearStall();
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 PUT ${xhr.status}`));
    };
    xhr.onerror = () => {
      clearStall();
      reject(new Error('S3 network error'));
    };
    xhr.onabort = clearStall;
    armStall();
    xhr.send(file);
  });

export function useAssetUpload(enterpriseId: string) {
  const { apolloClient } = useStores();
  const [requestUpload] = useEnterpriseAssetLibraryRequestUploadMutation({ client: apolloClient });
  const [finalize] = useEnterpriseAssetLibraryFinalizeAssetMutation({ client: apolloClient });

  const upload = useCallback(
    async (
      file: File,
      opts: { folderId?: string | null; onProgress?: (p: UploadProgress) => void } = {}
    ): Promise<UploadResult> => {
      const { onProgress, folderId } = opts;
      const report = (p: UploadProgress) => onProgress?.(p);

      try {
        report({ stage: 'hashing', percent: 0 });
        const [contentHash, dimensions] = await Promise.all([
          hashFile(file, (pct) => report({ stage: 'hashing', percent: pct })),
          readDimensions(file),
        ]);

        report({ stage: 'requesting', percent: 0 });
        const intentResult = await requestUpload({
          variables: {
            enterpriseId,
            fileName: file.name,
            mimeType: file.type,
            fileSize: file.size,
            contentHash,
          },
        });
        const intent = intentResult.data?.enterpriseAssetLibraryRequestUpload;
        if (!intent) throw new Error('Upload intent missing from response');

        if (intent.alreadyInLibrary && intent.existingAsset) {
          report({ stage: 'done', percent: 100 });
          return { asset: buildAsset(intent.existingAsset), alreadyInLibrary: true };
        }

        if (intent.uploadUrl && !intent.alreadyUploaded) {
          report({ stage: 'uploading', percent: 0 });
          await putToS3(intent.uploadUrl, file, contentHash, (pct) => report({ stage: 'uploading', percent: pct }));
        }

        report({ stage: 'finalizing', percent: 0 });
        const finalizeResult = await finalize({
          variables: {
            enterpriseId,
            contentHash,
            name: file.name.replace(/\.[^.]+$/, ''),
            fileName: file.name,
            mimeType: file.type,
            fileSize: file.size,
            folderId: folderId ?? null,
            tags: [],
            width: dimensions.width ?? null,
            height: dimensions.height ?? null,
            aiGenerated: false,
          },
        });

        const asset = finalizeResult.data?.enterpriseAssetLibraryFinalizeAsset;
        if (!asset) throw new Error('Finalize response missing asset');

        report({ stage: 'done', percent: 100 });
        return { asset: buildAsset(asset), alreadyInLibrary: false };
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        report({ stage: 'error', percent: 0, error: message });
        throw e;
      }
    },
    [enterpriseId, requestUpload, finalize]
  );

  return { upload, inferAssetType };
}
